<template>
    <div class="map-icons-new">
        <div class="search-container">
            <!-- Radio buttons acima da lupa -->
            <transition name="search-overlay">
                <div v-if="showSearch" class="search-type-overlay">
                    <a-radio-group
                        v-model:value="searchType"
                        size="small"
                        class="search-type-selector"
                    >
                        <a-radio-button value="address" :title="'Endereço'">
                            <i class="fas fa-location-dot"></i>
                        </a-radio-button>
                        <a-radio-button value="propertyId" :title="'Inscrição Municipal'">
                            <i class="fas fa-id-card"></i>
                        </a-radio-button>
                        <a-radio-button value="sql" :title="'Setor/Quadra/Lote'">
                            <i class="fas fa-layer-group"></i>
                        </a-radio-button>
                        <a-radio-button value="cnae" :title="'Código e Descrição CNAE'">
                            <i class="fas fa-briefcase"></i>
                        </a-radio-button>
                    </a-radio-group>
                </div>
            </transition>

            <!-- Lupa + campo de busca -->
            <div class="search-wrapper">
                <a-button class="map-icon-btn" @click="toggleSearch">
                    <template #icon>
                        <SearchOutlined class="menu-icon" />
                    </template>
                </a-button>

                <div v-if="showSearch && searchType === 'sql'" class="sql-input">
                    <div class="sql-field">
                        <span class="sql-addon">S:</span>
                        <a-input
                            v-model:value="setor"
                            :maxlength="4"
                            ref="setorSearch"
                            @change="fetchSqlSuggestion"
                        />
                    </div>

                    <div class="sql-field">
                        <span class="sql-addon">Q:</span>
                        <a-input
                            v-model:value="quadra"
                            :maxlength="4"
                            ref="quadraSearch"
                            @change="fetchSqlSuggestion"
                        />
                    </div>

                    <div class="sql-field">
                        <span class="sql-addon">L:</span>
                        <a-input
                            v-model:value="lote"
                            :maxlength="4"
                            ref="loteSearch"
                            @change="fetchSqlSuggestion"
                        />
                    </div>
                </div>

                <a-input
                    v-else-if="showSearch"
                    v-model:value="searchQuery"
                    class="search-input"
                    :placeholder="placeholder"
                    allow-clear
                    @input="handleInput"
                />
            </div>
        </div>

        <!-- Lista de sugestões -->
        <ul v-show="showSuggestions" class="suggestions">
            <li v-for="item in filteredSuggestions" :key="item.value" @click="handleSelect(item)">
                {{ item.value }}
            </li>
        </ul>
    </div>
</template>

<script setup>
import {ref, computed, watch, onMounted} from 'vue'
import { SearchOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { defineEmits } from 'vue'
import axios from "axios";

const props = defineProps({
    controls: Object
})

const emits = defineEmits(['selectSearchItem', 'changeSearchType'])

const cnaeSuggestions = ref([])
const searchQuery = ref('')
const showSearch = ref(true)
const searchType = ref('address')
const localSuggestions = ref([])
const setor = ref('')
const quadra = ref('')
const lote = ref('')
const setorSearch = ref(null)
const quadraSearch = ref(null)
const loteSearch = ref(null)

const suggestions = ref([])

const placeholder = computed(() => {
    switch (searchType.value) {
        case 'propertyId':
            return 'Inscrição Municipal'
        case 'address':
            return 'Endereço'
        case 'sql':
            return 'Setor/Quadra/Lote'
        case 'cnae':
            return 'Código ou Descrição CNAE'
        default:
            return 'Buscar...'
    }
})

const toggleSearch = () => {
    showSearch.value = !showSearch.value
    if (!showSearch.value) {
        searchQuery.value = ''
        setor.value = ''
        quadra.value = ''
        lote.value = ''
        localSuggestions.value = []
    }
}

const filteredSuggestions = computed(() => {
    const query = searchQuery.value.toString().toLowerCase()
    if (searchType.value === 'propertyId') {
        return suggestions.value
            .filter(item => item.value.toString().toLowerCase().includes(query))
            .slice(0, 10)
    }

    return localSuggestions.value
        .filter(item => item.value.toString().toLowerCase().includes(query))
        .slice(0, 10)
})

const showSuggestions = computed(() => {
    return showSearch.value && filteredSuggestions.value.length > 0 &&
        ['propertyId', 'cnae', 'sql', 'address'].includes(searchType.value)
})

watch(searchType, () => {
    searchQuery.value = ''
    setor.value = ''
    quadra.value = ''
    lote.value = ''
    emits('changeSearchType', searchType.value)
    localSuggestions.value = []
})

const handleInput = async () => {
    switch (searchType.value) {
        case 'sql':
            await fetchSqlSuggestion()
            break;
        case 'address':
            await fetchAddressSuggestions()
            break;
        case 'cnae':
            fetchCNAESuggestions()
            break;
    }
}

const fetchAddressSuggestions = async () => {
    if (!searchQuery.value?.trim()) {
        localSuggestions.value = []
        return
    }

    try {
        localSuggestions.value = (await window.axios.get(`/api/viabilidade/search/logradouro/${encodeURIComponent(searchQuery.value)}`)).data.data
    } catch (e) {
        message.error('Erro ao buscar endereços')
    }
}

const fetchSqlSuggestion = async () => {
    try {
        localSuggestions.value = (await window.axios.get(`/api/viabilidade/search/setor-quadra-lote/${setor.value}-${quadra.value}-${lote.value}`)).data.data
    } catch (e) {
        message.error('Erro ao buscar.')
    }
}

const fetchCNAESuggestions = () => {
    localSuggestions.value = cnaeSuggestions.value
}

const handleSelect = (item) => {
    searchQuery.value = item.value

    if (searchType.value === 'address') {
        if (item.propertyId) {
            emits('selectSearchItem', {
                propertyId: item.propertyId,
                searchType: searchType.value,
            })
        } else {
            emits('selectSearchItem', {
                streetId: item.streetId,
                searchType: searchType.value,
            })
        }
    } else {
        if (searchType.value === 'cnae') {
            emits('selectSearchItem', {
                cnaeId: item.cnaeId,
                searchType: searchType.value,
            })
        } else {
            emits('selectSearchItem', {
                propertyId: item.propertyId,
                searchType: searchType.value,
            })
        }

    }


    localSuggestions.value = []
}

const getPropertyIdsList = async () => {
    return (await window.axios.get('/api/maps/layers/property-ids')).data.data.map(prop => {
        return {
            propertyId: prop,
            value: prop
        }
    })
}

const getCnaes = () => {
    try {
        axios.get('/proxy/ibge/cnae/classes').then(data => {
            data.data.forEach(cnae => {
                axios.get(`/proxy/ibge/cnae/classes/${cnae.id}/subclasses`).then(data => {
                    data.data.forEach(cnae => {
                        cnaeSuggestions.value.push({
                            value: `${cnae.id} - ${cnae.descricao}`,
                            description: cnae.descricao,
                            cnaeId: cnae.id
                        })
                    })
                })
            })

        })
    } catch (e) {
        message.error('Erro ao buscar CNAEs')
    }
}

defineExpose({cnaeSuggestions})

watch(setor, (val) => (val?.toString().length === 4) ? quadraSearch.value?.focus() : '')

watch(quadra, (val) => {
    const length = val?.toString().length || 0

    if (length === 4) {
        loteSearch.value?.focus()
    } else if (length === 0) {
        setorSearch.value?.focus()
    }
})

watch(lote, (val) => (val?.toString().length === 0) ? quadraSearch.value?.focus() : '')

onMounted(async () => {
    getCnaes()
    suggestions.value = await getPropertyIdsList()
})

</script>

<style scoped>
.map-icons-new {
    max-width: 600px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.search-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
}

.search-type-overlay {
    background: white;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    padding: calc(var(--home-map-icon-size, 32px) * 0.06);
    margin-bottom: 4px;
    overflow: hidden;
    transform-origin: top left;
    z-index: 1000;
}

.map-icon-btn {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
    border-radius: var(--home-map-icon-radius, 6px);
    background-color: rgba(0, 60, 136, .6);
    color: white;
    border: 2px solid white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.map-icon-btn:hover {
    background-color: rgba(0, 60, 136, .7);
}

.menu-icon,
.map-icon-btn i {
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.search-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
}

.search-input {
    width: 350px;
    transition: all 0.3s ease;
}

:deep(.search-input.ant-input),
:deep(.search-input.ant-input-affix-wrapper) {
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
}

:deep(.search-input.ant-input-affix-wrapper) {
    padding-top: 0;
    padding-bottom: 0;
}

:deep(.search-input.ant-input-affix-wrapper .ant-input) {
    height: 100%;
    line-height: normal;
}

.suggestions {
    list-style: none;
    padding: 4px;
    margin-top: 4px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
    max-height: 150px;
    overflow-y: auto;
    width: 100%;
}

.suggestions li {
    padding: 6px;
    cursor: pointer;
}

.suggestions li:hover {
    background-color: #f0f0f0;
}

.search-type-selector {
    display: flex;
    flex-direction: row;
    gap: 6px;
}

.search-type-selector .ant-radio-button-wrapper {
    box-sizing: border-box;
    min-width: calc(var(--home-map-icon-size, 32px) * 0.75);
    padding: 0 calc(var(--home-map-icon-size, 32px) * 0.18);
    font-size: calc(var(--home-map-icon-glyph-size, 16px) * 0.75);
    height: calc(var(--home-map-icon-size, 32px) * 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
}

.search-type-selector .ant-radio-button-wrapper i {
    pointer-events: auto;
    font-size: calc(var(--home-map-icon-glyph-size, 16px) * 0.85);
}

.search-overlay-enter-active,
.search-overlay-leave-active {
    transition:
        opacity 0.24s ease,
        transform 0.24s ease,
        max-height 0.24s ease,
        margin-bottom 0.24s ease,
        padding-top 0.24s ease,
        padding-bottom 0.24s ease;
}

.search-overlay-enter-from,
.search-overlay-leave-to {
    opacity: 0;
    transform: translateY(-8px) scaleY(0.92);
    max-height: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
}

.search-overlay-enter-to,
.search-overlay-leave-from {
    opacity: 1;
    transform: translateY(0) scaleY(1);
    max-height: 48px;
}

:deep(.ant-input-group-addon),
:deep(.ant-input-number-group-addon) {
    background-color: #f5f5f5;
}

.sql-input {
    display: flex;
    align-items: stretch;
    flex-wrap: nowrap;
    gap: 8px;
    width: 350px;
    min-width: 350px;
}

.sql-field {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    background: #fff;
}

.sql-addon {
    flex: 0 0 auto;
    padding: 0 8px;
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    background-color: #f5f5f5;
    border-right: 1px solid #d9d9d9;
    color: rgba(0, 0, 0, 0.88);
    font-size: 12px;
}

.sql-field :deep(.ant-input) {
    width: 100%;
    min-width: 0;
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
    text-align: center;
    border: 0;
    border-radius: 0;
    box-shadow: none;
}

.sql-field :deep(.ant-input:focus),
.sql-field :deep(.ant-input-focused) {
    box-shadow: none;
}
</style>
